import { FieldStateV3, FormContext, FormStateV3 } from '../../../../../commonv3/states/form/FormStateV3';
import { CurrentUserState } from '../../../../../mainv2/states/CurrentUserState';
import { action, observable } from 'mobx';
import { CardSummary } from '../../../../../mainv2/types/card';
import { UserCardApis } from '../../../../apis/users';

type CardInput = {
    number: string;
    mm: string;
    yy: string;
    cvc: string;
};

type CardFormSchema = {
    number: FieldStateV3<string>;
    mm: FieldStateV3<string>;
    yy: FieldStateV3<string>;
    cvc: FieldStateV3<string>;
};

export class CreditCardFormState extends FormStateV3<CardInput, CardFormSchema> {
    constructor() {
        super({
            number: new FieldStateV3<string>([]),
            mm: new FieldStateV3<string>([]),
            yy: new FieldStateV3<string>([]),
            cvc: new FieldStateV3<string>([]),
        });
    }
    init() {
        this._init('card', new FormContext(), {
            number: '',
            mm: '',
            yy: '',
            cvc: '',
        });
    }
}

export class CreditCardState {
    userState: CurrentUserState;
    cardFormState: CreditCardFormState;
    @observable editMode: boolean;
    @observable newToken: string;
    @observable card: CardSummary;

    constructor(userState: CurrentUserState) {
        this.userState = userState;
        this.cardFormState = new CreditCardFormState();
        this.editMode = false;
    }

    async init() {
        this.cardFormState.init();
        if (this.userState.hasCard()) {
            this.editMode = false;
            this.card = await UserCardApis.getCurrentCard();
            if (!this.card) {
                this.editMode = true;
            }
        } else {
            this.editMode = true;
        }
    }

    async validateNewCard() {
        try {
            const response = await this.cardFormState.submitAsJson(
                async (json) =>
                    await UserCardApis.createToken({
                        number: json.number,
                        exp_month: json.mm,
                        exp_year: json.yy,
                        cvc: json.cvc,
                    })
            );
            this.newToken = response.id;
            this.card = response.card;
            return true;
        } catch (error) {
            const defaultErrors = { number: [], mm: [], yy: [], cvc: [] };
            switch (error.code) {
                case 'invalid_expiry_year':
                    this.cardFormState.setErrors({ ...defaultErrors, yy: ['正しく入力してください'] });
                    break;
                case 'invalid_expiry_month':
                    this.cardFormState.setErrors({ ...defaultErrors, mm: ['正しく入力してください'] });
                    break;
                case 'card_declined':
                    this.cardFormState.setErrors({
                        ...defaultErrors,
                        number: [
                            'カード会社に支払いを拒否されました。限度額をご確認の上、カード会社にお問い合わせください。',
                        ],
                    });
                    break;
                case 'incorrect_card_data':
                default:
                    this.cardFormState.setErrors({ ...defaultErrors, number: ['カード情報が正しくありません'] });
            }
            this.newToken = null;
            return false;
        }
    }

    async update() {
        if (!this.newToken) {
            throw new Error('New token must be set before updating card');
        }
        await UserCardApis.updateCard(this.newToken);
    }

    hasErrors() {
        return this.editMode && this.cardFormState.hasErrors();
    }

    @action.bound
    setEditMode(editMode: boolean) {
        this.editMode = editMode;
    }
}
